import { useEffect, useState } from 'react';
import 'twin.macro';
import {
  useNetworkDiagnostics,
  selectWebSocketSnapshot,
  selectRestSnapshot,
  selectMediaSnapshot,
  selectWebRtcSnapshot,
} from '../../zustand/network-diagnostics-store';
import { getRealtimeAudioReport } from '../../services/realtime/debug';
import NetworkSpeedTest from '../network-speed-test';
import {
  startResourceTimingObserver,
  stopResourceTimingObserver,
} from '../../services/diagnostics/resource-timing-observer';

const STORAGE_KEY = 'network-debug';
const POLL_INTERVAL_MS = 700;

const readStored = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (_error) {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be unavailable; the in-memory toggle still works.
  }
};

// Visible by default, same convention as RealtimeDebugPanel — '0' is stored
// on close, so hiding it sticks across reloads within a dev session.
const readInitialVisibility = () => {
  try {
    if (new URLSearchParams(window.location.search).get('networkDebug') === '1') {
      return true;
    }
  } catch (_error) {
    /* no location in a non-browser context */
  }

  const stored = readStored(STORAGE_KEY, null);
  if (stored === '1') {
    return true;
  }
  if (stored === '0') {
    return false;
  }

  return true;
};

const fmt = (value, digits = 0) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value !== 'number') {
    return String(value);
  }
  return digits > 0 ? value.toFixed(digits) : String(Math.round(value));
};

const Line = ({ children }) => <div tw="whitespace-pre">{children}</div>;

/**
 * Client-side only — builds a JSON file from what's already in memory and
 * triggers a browser download. No network request, no backend persistence
 * (Step 3's own rule: "prefer local/in-memory diagnostics for this step").
 */
const exportReport = (records, snapshots, webrtcReport) => {
  try {
    const report = {
      generatedAt: new Date().toISOString(),
      recordCount: records.length,
      snapshots,
      webrtc: webrtcReport,
      records,
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `network-diagnostics-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('[network-diagnostics] export failed', error);
  }
};

/**
 * Raw runtime measurements only — no GOOD/BAD/PASS/FAIL, no score, no
 * threshold comparison. This is the "Debug UI" required by Step 3 of the
 * network connectivity investigation: developer-only, never mounted for a
 * real patient (gated by FEATURES.NETWORK_DIAGNOSTICS in App.jsx, the same
 * flag that turns the store's recorders on — off in production).
 *
 * Toggle with Ctrl+Alt+N, `?networkDebug=1`, or
 * `localStorage.setItem('network-debug','1')`.
 */
const NetworkDiagnosticsPanel = () => {
  const [visible, setVisible] = useState(readInitialVisibility);
  const [collapsed, setCollapsed] = useState(false);
  const [webrtcReport, setWebrtcReport] = useState(null);

  const records = useNetworkDiagnostics(state => state.records);

  useEffect(() => {
    const handleKeyDown = event => {
      // event.code, not event.key — see RealtimeDebugPanel for why (non-Latin
      // keyboard layouts don't produce a stable 'n' from event.key).
      if (event.ctrlKey && event.altKey && event.code === 'KeyN') {
        event.preventDefault();
        setVisible(previous => {
          const next = !previous;
          writeStored(STORAGE_KEY, next ? '1' : '0');
          return next;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // The resource-timing observer only runs while this panel is actually
  // mounted — never for a real patient, and stopped the moment the panel is
  // torn down (route change, flag off).
  useEffect(() => {
    const stop = startResourceTimingObserver();
    return stop;
  }, []);

  useEffect(() => {
    if (!visible || collapsed) {
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      const report = await getRealtimeAudioReport();
      if (!cancelled) {
        setWebrtcReport(report);
      }
    };

    tick();
    const timer = window.setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [visible, collapsed]);

  if (!visible) {
    return null;
  }

  if (collapsed) {
    return (
      <button
        type="button"
        tw="fixed bottom-2 left-2 z-[99998] grid h-12 w-12 place-items-center rounded-[14px] bg-black/85 text-[22px] leading-none text-[#e2e8f0]"
        style={{ backdropFilter: 'blur(2px)' }}
        title="expand the network diagnostics panel"
        aria-label="expand the network diagnostics panel"
        onClick={() => setCollapsed(false)}
      >
        ›
      </button>
    );
  }

  const ws = selectWebSocketSnapshot(records);
  const rest = selectRestSnapshot(records);
  const media = selectMediaSnapshot(records);
  const webrtcDerived = selectWebRtcSnapshot(records);
  const inbound = webrtcReport?.inboundAudio ?? null;

  return (
    <div
      tw="fixed bottom-2 left-2 z-[99998] max-h-[85vh] w-[340px] max-w-[calc(100vw-1rem)] overflow-auto rounded-lg bg-black/85 p-3 font-mono text-[11px] leading-4 text-white"
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <div tw="mb-2 flex items-center justify-between">
        <strong>NETWORK DIAGNOSTICS</strong>
        <div tw="flex gap-2">
          <button
            type="button"
            tw="text-[#94a3b8]"
            title="download a JSON report of everything recorded this session"
            aria-label="export network diagnostics report"
            onClick={() =>
              exportReport(records, { ws, rest, media, webrtc: webrtcDerived }, webrtcReport)
            }
          >
            ⬇
          </button>
          <button
            type="button"
            tw="text-[#94a3b8]"
            title="collapse to a handle"
            aria-label="collapse the network diagnostics panel"
            onClick={() => setCollapsed(true)}
          >
            ⌄
          </button>
          <button
            type="button"
            tw="text-[#94a3b8]"
            onClick={() => {
              writeStored(STORAGE_KEY, '0');
              setVisible(false);
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <NetworkSpeedTest sessionTraffic={{ downKBs: ws.downloadKBs, upKBs: ws.uploadKBs }} />

      <div tw="mt-2 border-t border-white/20 pt-2 text-[#94a3b8]">WebSocket</div>
      <Line>Connection: {ws.connectionState}</Line>
      <Line>Setup: {ws.setupMs === null ? '—' : `${fmt(ws.setupMs)} ms`}</Line>
      <Line>Messages/sec: {fmt(ws.messagesPerSec, 1)}</Line>
      <Line>Upload: {fmt(ws.uploadKBs, 2)} KB/s</Line>
      <Line>Download: {fmt(ws.downloadKBs, 2)} KB/s</Line>
      <Line>RTT P50: {ws.rttP50 === null ? '—' : `${fmt(ws.rttP50)} ms`}</Line>
      <Line>RTT P95: {ws.rttP95 === null ? '—' : `${fmt(ws.rttP95)} ms`}</Line>
      <Line>Reconnects: {ws.reconnectCount}</Line>

      <div tw="mt-2 border-t border-white/20 pt-2 text-[#94a3b8]">REST</div>
      <Line>Requests: {rest.requestCount}</Line>
      <Line>Errors: {rest.errorCount}</Line>
      <Line>
        Avg duration: {rest.avgDurationMs === null ? '—' : `${fmt(rest.avgDurationMs)} ms`}
      </Line>
      <Line>
        P95 duration: {rest.p95DurationMs === null ? '—' : `${fmt(rest.p95DurationMs)} ms`}
      </Line>
      <Line>TTFB P95: {rest.ttfbP95Ms === null ? '—' : `${fmt(rest.ttfbP95Ms)} ms`}</Line>

      <div tw="mt-2 border-t border-white/20 pt-2 text-[#94a3b8]">Media</div>
      <Line>
        MediaPipe download:{' '}
        {media.mediaPipeBytes === null ? '—' : `${(media.mediaPipeBytes / 1e6).toFixed(1)} MB`}
      </Line>
      <Line>
        Duration:{' '}
        {media.mediaPipeDurationMs === null
          ? '—'
          : `${(media.mediaPipeDurationMs / 1000).toFixed(1)} s`}
      </Line>
      <Line>
        Effective throughput:{' '}
        {media.mediaPipeThroughputMbps === null
          ? '—'
          : `${fmt(media.mediaPipeThroughputMbps, 2)} Mbps`}
      </Line>
      <Line>Video waiting events: {media.videoWaitingCount}</Line>
      <Line>Video errors: {media.videoErrorCount}</Line>

      <div tw="mt-2 border-t border-white/20 pt-2 text-[#94a3b8]">WebRTC (voice)</div>
      <Line>ICE state: {webrtcReport?.connection.iceConnectionState ?? '—'}</Line>
      <Line>Connection state: {webrtcReport?.connection.connectionState ?? '—'}</Line>
      <Line>
        Setup: {webrtcDerived.setupMs === null ? '—' : `${fmt(webrtcDerived.setupMs)} ms`}
      </Line>
      <Line>
        RTT: {inbound?.rtt === undefined || inbound?.rtt === null ? '—' : `${fmt(inbound.rtt)} ms`}
      </Line>
      <Line>
        Jitter:{' '}
        {inbound?.jitter === null || inbound?.jitter === undefined
          ? '—'
          : `${fmt(inbound.jitter * 1000)} ms`}
      </Line>
      <Line>Packets lost: {fmt(inbound?.packetsLost)}</Line>
      <Line>Codec: {inbound?.codec ?? '—'}</Line>
      <Line>
        Inbound bitrate:{' '}
        {webrtcDerived.inboundBitrateKbps === null
          ? '—'
          : `${fmt(webrtcDerived.inboundBitrateKbps, 1)} kbps`}
      </Line>
      <Line>
        Reconnecting:{' '}
        {webrtcReport?.connection.reconnecting
          ? `yes (attempt ${webrtcReport.connection.reconnectAttempt})`
          : 'no'}
      </Line>
    </div>
  );
};

export default NetworkDiagnosticsPanel;
